import { NotificationVariants, type CareOrder, type Security } from '@talos/kyoko';

import { acceptMultipleQuotes, type AcceptMultipleQuotesDeps } from './acceptMultipleQuotes';
import { quoteToAcceptQuoteParams, type AcceptQuoteParams } from './acceptQuote';
import { RFQSweeperFillExecutionStatus, type RFQSweeper, type RFQSweeperFillExecutionState } from './RFQSweeper';

/**
 * Execution bridge for RFQ sweeping.
 *
 * `RFQSweeper` owns the immutable staging state: selected markets, editable quantities,
 * validation, and per-fill execution statuses. This module owns the side effects needed
 * to turn that state into accept requests. Keep API concerns here so the sweeper model
 * stays easy to test and reason about.
 */
export interface ExecuteRFQSweeperParams extends AcceptMultipleQuotesDeps {
  sweeper: RFQSweeper;
  security: Security;
  allowedSlippage?: string;
  truncateCareOrderID: boolean;
  associatedCareOrder?: CareOrder;
  onUpdate: (sweeper: RFQSweeper) => void;
}

/**
 * Executes the fills in an `RFQSweeper`.
 *
 * The function reports progress through `onUpdate`:
 *
 * 1. mark fills pending before publishing accept requests
 * 2. call `acceptMultipleQuotes`
 * 3. mark each pending fill filled or error using the result for that market
 *
 * Each fill carries an `RFQSweeperQuote`, so request construction uses the same parent
 * quote metadata as the selected quote market. That pairing is what keeps RFQID/QuoteID
 * correct when the sweep is built from multiple quote rows.
 */
export async function executeRFQSweeper({
  sweeper,
  security,
  allowedSlippage,
  truncateCareOrderID,
  associatedCareOrder,
  onUpdate,
  ...acceptMultipleQuotesDeps
}: ExecuteRFQSweeperParams): Promise<void> {
  if (sweeper.sweepDisabled) {
    return;
  }

  const paramsList: AcceptQuoteParams[] = [];
  const requestMarketNames: string[] = [];

  for (const fill of sweeper.fills) {
    const params = quoteToAcceptQuoteParams({
      quote: fill.quote.quote,
      childQuote: fill.quote.quoteMarket,
      side: sweeper.side,
      price: fill.px,
      tradeBest: false,
      allowedSlippage,
      orderQty: fill.desiredQty.value!,
      truncateCareOrderID,
      security,
      associatedCareOrder,
    });

    if (!params) {
      acceptMultipleQuotesDeps.addToast({
        text: `Cannot sweep RFQ: failed to build accept request for ${fill.quote.quoteMarket.Market}.`,
        variant: NotificationVariants.Negative,
        dismissable: true,
        timeout: 5000,
      });
      onUpdate(
        sweeper.markMarketsWithExecutionStatus(
          new Map([
            [
              fill.quote.quoteMarket.Market,
              {
                status: RFQSweeperFillExecutionStatus.Error,
                tooltip: `Failed to build accept request for ${fill.quote.quoteMarket.Market}.`,
              },
            ],
          ])
        )
      );
      return;
    }

    paramsList.push(params);
    requestMarketNames.push(fill.quote.quoteMarket.Market);
  }

  const pendingSweeper = sweeper.markMarketsPending(requestMarketNames);
  onUpdate(pendingSweeper);

  const result = await acceptMultipleQuotes(paramsList, acceptMultipleQuotesDeps);
  onUpdate(
    pendingSweeper.markMarketsWithExecutionStatus(
      new Map<string, RFQSweeperFillExecutionState>(
        result.results.map(result =>
          result.status === 'fulfilled'
            ? [result.params.childQuote.Market, { status: RFQSweeperFillExecutionStatus.Filled }]
            : [
                result.params.childQuote.Market,
                {
                  status: RFQSweeperFillExecutionStatus.Error,
                  tooltip: result.reason.text,
                },
              ]
        )
      )
    )
  );
}
